import React, { useEffect, useState } from "react";import { useOutletContext } from "react-router-dom";
import TimeOfDayBadge from "@/components/TimeOfDayBadge";
import { N8 } from "@/components/BrandMark";
import { endpoints } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Moon, Copy, Check as CheckIcon, Camera, X as XIcon, Sparkles, ChevronRight, Feather, Mic } from "lucide-react";
import AuthorAvatar from "@/components/AuthorAvatar";
import MediaLibrary from "@/components/MediaLibrary";
import SacredAgingProfile, { SACRED_AGING_SECTIONS } from "@/components/SacredAgingProfile";
import BecomingGallery from "@/components/BecomingGallery";
import N8VoiceSettings from "@/components/N8VoiceSettings";
import PushNotificationSettings from "@/components/PushNotificationSettings";
import AccountPanel from "@/components/AccountPanel";
import RedeemProCodePanel from "@/components/RedeemProCodePanel";
import AdminProCodes from "@/components/AdminProCodes";
import AdminMirrorUsage from "@/components/AdminMirrorUsage";
import { useSubscription } from "@/context/SubscriptionContext";
import WellnessAccessPanel from "@/components/WellnessAccessPanel";
import { useAuth } from "@/context/AuthContext";
import ProGate from "@/components/ProGate";
import ProBadge from "@/components/ProBadge";
import PageHero from "@/components/PageHero";
import { HERO } from "@/lib/heroImages";

export default function Profile() {
  const outletCtx = useOutletContext() || {};
  const { user } = useAuth();
  const sub = useSubscription();
  const [profile, setProfile] = useState(null);
  const [meta, setMeta] = useState({ age_groups: [], goal_tags: [] });
  const [edits, setEdits] = useState({});
  const [copiedCode, setCopiedCode] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [view, setView] = useState("main"); // "main" | "sacred" | "becoming"

  const uploadAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Only images."); return; }
    if (file.size > 6 * 1024 * 1024) { toast.error("Max 6MB."); return; }
    setUploadingAvatar(true);
    try {
      const { data } = await endpoints.uploadPhoto(file);
      await endpoints.updateProfile({ avatar_url: data.path });
      toast.success("Photo set.");
      await load();
    } catch {
      toast.error("Could not upload.");
    } finally {
      setUploadingAvatar(false);
      e.target.value = "";
    }
  };

  const removeAvatar = async () => {
    setUploadingAvatar(true);
    try {
      await endpoints.updateProfile({ avatar_url: null });
      toast.success("Photo removed.");
      await load();
    } catch {
      toast.error("Could not remove.");
    } finally {
      setUploadingAvatar(false);
    }
  };

  const copyClientCode = async () => {
    if (!profile?.client_code) return;
    try {
      await navigator.clipboard.writeText(profile.client_code);
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 1600);
      toast.success("Wellness code copied.");
    } catch {
      toast.error("Copy blocked — select and copy manually.");
    }
  };

  const load = async () => {
    const [p, m] = await Promise.all([endpoints.profile(), endpoints.meta()]);
    setProfile(p.data);
    setEdits({});
    setMeta(m.data);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (Object.keys(edits).length === 0) return;
    await endpoints.updateProfile(edits);
    toast.success("Profile saved.");
    load();
  };

  if (!profile) return <div className="text-sm text-[hsl(var(--muted-foreground))]" data-testid="profile-loading">Steeping…</div>;

  if (view === "sacred") {
    return (
      <SacredAgingProfile
        profile={profile}
        onBack={() => setView("main")}
        onSaved={load}
      />
    );
  }

  if (view === "becoming") {
    return <BecomingGallery onBack={() => setView("main")} />;
  }

  const val = (k) => (edits[k] !== undefined ? edits[k] : profile[k]);
  const set = (k, v) => setEdits((e) => ({ ...e, [k]: v }));

  return (
    <div className="space-y-12" data-testid="profile-page">
      <PageHero
        emblem=""
        eyebrow={
          <span className="inline-flex items-center gap-3">
            <span>You</span>
            <span className="opacity-50" aria-hidden>·</span>
            <TimeOfDayBadge testid="profile-time-of-day" />
          </span>
        }
        title={profile.name}
        supporting={profile.intention || undefined}
        image={HERO.profile}
        testid="profile-hero"
      />

      <header className="reveal">
        <div className="flex items-center gap-5 flex-wrap">
          <div className="relative shrink-0" data-testid="profile-avatar-block">
            <AuthorAvatar
              name={profile.name}
              src={profile.avatar_url}
              size="lg"
              ring="clay"
              testid="profile-avatar-lg"
            />
            <label
              data-testid="profile-avatar-upload"
              className={`absolute -bottom-1 -right-1 w-8 h-8 rounded-full grid place-items-center cursor-pointer border hairline bg-[hsl(var(--paper))] hover:bg-[hsl(var(--sand))] tab-link ${uploadingAvatar ? "opacity-60 pointer-events-none" : ""}`}
              title={profile.avatar_url ? "Replace photo" : "Upload photo"}
            >
              <Camera size={13} className="text-[hsl(var(--clay))]" />
              <input
                type="file"
                accept="image/*"
                onChange={uploadAvatar}
                data-testid="profile-avatar-input"
                className="hidden"
              />
            </label>
          </div>
          <div className="min-w-0">
            <h2 className="font-display text-3xl md:text-4xl tracking-tight leading-[1.05]">{profile.name}</h2>
            {profile.avatar_url && (
              <button
                onClick={removeAvatar}
                disabled={uploadingAvatar}
                data-testid="profile-avatar-remove"
                className="mt-2 text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--clay))] inline-flex items-center gap-1"
              >
                <XIcon size={10} /> Remove photo
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Your details — moved to the top of Profile so editing feels immediate. */}

      <section className="p-8 border hairline rounded-md bg-[hsl(var(--paper))] reveal reveal-2" data-testid="profile-form">
        <div className="text-[11px] uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">Edit</div>
        <h2 className="font-display text-3xl mt-1">Your details</h2>

        <div className="mt-6 grid md:grid-cols-2 gap-6">
          <Field label="Display name" hint="Shows publicly. Doesn't need to be unique — pick what feels like you.">
            <Input value={val("display_name") ?? val("name") ?? ""} onChange={(e) => set("display_name", e.target.value)} data-testid="input-display-name" className="bg-transparent" />
          </Field>
          <Field label="Username" hint="Your unique handle in The Collective. Letters, numbers, underscore or period.">
            <UsernameInput
              currentUsername={profile?.username || ""}
              value={val("username") ?? profile?.username ?? ""}
              onChange={(v) => set("username", v)}
            />
          </Field>
          <Field label="Birthday">
            <Input
              value={val("birthday") || ""}
              onChange={(e) => set("birthday", e.target.value)}
              placeholder="1982-06-14"
              data-testid="input-birthday"
              className="bg-transparent"
            />
          </Field>

          <div className="md:col-span-2">
            <Field label="Age group">
              <div className="flex flex-wrap gap-2" data-testid="profile-age-groups">
                {meta.age_groups.map((a) => {
                  const on = (val("age_group") || "") === a;
                  return (
                    <button
                      key={a}
                      type="button"
                      onClick={() => set("age_group", a)}
                      data-testid={`profile-age-${a}`}
                      className={`px-3.5 py-1.5 rounded-full text-sm border hairline ${
                        on ? "bg-[hsl(var(--fg))] text-[hsl(var(--paper))] border-transparent" : "hover:bg-[hsl(var(--sand))]"
                      }`}
                    >
                      {a}
                    </button>
                  );
                })}
              </div>
            </Field>
          </div>

          <div className="md:col-span-2">
            <Field
              label="Gender"
              hint={<>Helps <N8 /> Guide keep recommendations, affirmations, and Future Self whispers linguistically accurate and physiologically-aware. Optional.</>}
            >
              <div className="flex flex-wrap gap-2" data-testid="profile-gender">
                {[
                  { slug: "woman",              label: "Woman" },
                  { slug: "man",                label: "Man" },
                  { slug: "nonbinary",          label: "Non-binary" },
                  { slug: "prefer_not_to_say",  label: "Prefer not to say" },
                ].map((g) => {
                  const on = (val("gender") || "") === g.slug;
                  return (
                    <button
                      key={g.slug}
                      type="button"
                      onClick={() => set("gender", on ? "" : g.slug)}
                      data-testid={`profile-gender-${g.slug}`}
                      className={`px-3.5 py-1.5 rounded-full text-sm border hairline ${
                        on ? "bg-[hsl(var(--fg))] text-[hsl(var(--paper))] border-transparent" : "hover:bg-[hsl(var(--sand))]"
                      }`}
                    >
                      {g.label}
                    </button>
                  );
                })}
              </div>
            </Field>
          </div>

          <div className="md:col-span-2">
            <div
              className="p-5 rounded-md bg-[hsl(var(--sand))]/30 border hairline"
              data-testid="profile-goals-moved-note"
            >
              <div className="text-[10px] uppercase tracking-[0.24em] text-[hsl(var(--copper))]">
                Goals live in Becoming now
              </div>
              <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))] leading-relaxed">
                Your chosen areas — skin, fitness, nutrition &amp; hormones, emotional
                wellbeing, sleep and mindset — are all tended in one place inside
                Becoming → Choose → Your Current Goals. Everything you save there
                shapes every recommendation across N8ONE.
              </p>
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <Button
            onClick={async () => {
              await endpoints.updateProfile({ onboarded: false });
              if (outletCtx.refetchShell) await outletCtx.refetchShell();
              toast("Welcome flow reopened.");
            }}
            variant="outline"
            data-testid="btn-redo-onboarding"
            className="rounded-full"
          >
            Redo welcome
          </Button>
          <Button
            onClick={save}
            data-testid="btn-save-profile"
            className="rounded-full bg-[hsl(var(--clay))] hover:bg-[hsl(var(--clay))]/90 text-[hsl(var(--paper))]"
            disabled={Object.keys(edits).length === 0}
          >
            Save
          </Button>
        </div>
      </section>


      <SacredAgingEntry
        profile={profile}
        onOpen={() => setView("sacred")}
      />


      <BecomingEntry onOpen={() => setView("becoming")} />


      <section className="p-8 border hairline rounded-md bg-[hsl(var(--paper))] reveal reveal-2" data-testid="client-code-section">
        <div className="flex items-baseline justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">For your wellness practice</div>
            <div className="font-display text-3xl mt-1">Wellness code</div>
            <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1 max-w-lg">
              Share this eight-character code with your wellness practice so they can send before &amp; after photos directly into
              your Becoming journey. Only they need it — you never have to type it.
            </p>
          </div>
        </div>
        <div className="mt-5 flex items-center gap-3 flex-wrap">
          <div
            data-testid="client-code-value"
            className="px-5 py-3 border hairline rounded-md bg-[hsl(var(--sand))]/40 font-display text-3xl tracking-[0.35em]"
          >
            {profile.client_code || "—"}
          </div>
          <Button
            onClick={copyClientCode}
            data-testid="btn-copy-client-code"
            className="rounded-full bg-[hsl(var(--clay))] hover:bg-[hsl(var(--clay))]/90 text-[hsl(var(--paper))]"
          >
            {copiedCode ? <CheckIcon size={14} className="mr-2" /> : <Copy size={14} className="mr-2" />}
            {copiedCode ? "Copied" : "Copy code"}
          </Button>
        </div>
      </section>

      <WellnessAccessPanel />


      <MediaLibrary />

      <AccountPanel />

      <RedeemProCodePanel />

      {user?.role === "admin" && (
        <>
          <AdminMirrorUsage />
          <AdminProCodes />
        </>
      )}

      <section className="p-8 border hairline rounded-md bg-[hsl(var(--paper))] reveal reveal-3" data-testid="reminders-section">
        <div className="text-[11px] uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">Reminders</div>

        <div className="mt-8 pt-6 border-t hairline" data-testid="quiet-hours-section">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <div className="font-display text-2xl inline-flex items-center gap-2">
                <Moon size={18} className="text-[hsl(var(--clay))]" /> Quiet hours
              </div>
              <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
                Mark a window where reminders stay silent. Anything due during this time fires the moment quiet hours end.
              </p>
            </div>
            <button
              type="button"
              onClick={() => set("quiet_hours_enabled", !(val("quiet_hours_enabled")))}
              data-testid="btn-toggle-quiet"
              aria-pressed={!!val("quiet_hours_enabled")}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border hairline tab-link ${
                val("quiet_hours_enabled")
                  ? "bg-[hsl(var(--clay))] text-[hsl(var(--paper))] border-transparent"
                  : "hover:bg-[hsl(var(--sand))]"
              }`}
            >
              {val("quiet_hours_enabled") ? "On" : "Off"}
            </button>
          </div>

          <div className="mt-5 grid sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))]">From</span>
              <Input
                type="time"
                value={val("quiet_hours_start") || ""}
                onChange={(e) => set("quiet_hours_start", e.target.value)}
                data-testid="quiet-hours-start"
                disabled={!val("quiet_hours_enabled")}
                className="bg-transparent mt-2"
              />
            </label>
            <label className="block">
              <span className="text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))]">Until</span>
              <Input
                type="time"
                value={val("quiet_hours_end") || ""}
                onChange={(e) => set("quiet_hours_end", e.target.value)}
                data-testid="quiet-hours-end"
                disabled={!val("quiet_hours_enabled")}
                className="bg-transparent mt-2"
              />
            </label>
          </div>
          <div className="mt-3 text-xs text-[hsl(var(--muted-foreground))]">
            A window that crosses midnight is fine — e.g. 22:00 to 07:00. Save with the button below.
          </div>
        </div>

        {/* Mirror Wrap Email — Pro-only, opt-in. Kept in the notifications
            block since it's a notification a member chooses to receive. */}
        <div className="mt-8 pt-6 border-t hairline" data-testid="mirror-wrap-email-section">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <div className="font-display text-2xl inline-flex items-center gap-2">
                <Sparkles size={18} className="text-[hsl(var(--clay))]" /> Mirror wrap emails
                <span
                  className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--terracotta))]"
                  data-testid="mirror-wrap-email-pro-badge"
                >
                  · Pro
                </span>
              </div>
              <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1 max-w-xl">
                When you close a Mirror session, quietly receive a soft take-away by email —
                what surfaced, what you recognized, and what may support you next.
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                if (!sub.isPro) {
                  sub.openPricing({
                    feature: "mirror_wrap_email",
                    title: "Receive quiet Mirror wrap-ups by email.",
                    blurb: "When a reflection closes, N8ONE can gather what surfaced and send it back to you — so nothing you name in The Mirror gets lost.",
                  });
                  return;
                }
                set("mirror_email_optin", !(val("mirror_email_optin")));
              }}
              data-testid="btn-toggle-mirror-wrap-email"
              aria-pressed={!!val("mirror_email_optin")}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border hairline tab-link ${
                sub.isPro && val("mirror_email_optin")
                  ? "bg-[hsl(var(--clay))] text-[hsl(var(--paper))] border-transparent"
                  : "hover:bg-[hsl(var(--sand))]"
              }`}
            >
              {sub.isPro
                ? (val("mirror_email_optin") ? "On" : "Off")
                : "Try Pro"}
            </button>
          </div>
        </div>

        {/* Intelligent Reminders (Pro) — surface below the manual controls
            so Free users still get the full manual experience above. */}
        <div className="mt-6">
          <ProGate
            feature="intelligent_reminders"
            title="Let your reminders evolve with you."
            blurb="Your reminders already work. Pro learns when you actually complete rituals and gently proposes a better time as your rhythm shifts — you always stay in control, nothing changes unless you tap accept."
            bullets={[
              "Adaptive timing suggestions based on your completions",
              "Ritual-based and goal-based reminder recommendations",
              "Never changes an important reminder without your approval",
            ]}
            testid="pro-gate-intelligent_reminders"
          >
            <IntelligentRemindersHint />
          </ProGate>
        </div>
      </section>

      <N8VoiceSettings />
      <PushNotificationSettings />

      <section
        className="pt-6 border-t hairline flex flex-wrap items-center gap-x-6 gap-y-2 text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--muted-foreground))]"
        data-testid="profile-legal-links"
      >
        <a
          href="/legal/privacy"
          target="_blank"
          rel="noopener noreferrer"
          className="hover:text-[hsl(var(--fg))]"
          data-testid="profile-privacy-link"
        >
          Privacy Policy
        </a>
        <a
          href="/legal/terms"
          target="_blank"
          rel="noopener noreferrer"
          className="hover:text-[hsl(var(--fg))]"
          data-testid="profile-terms-link"
        >
          Terms &amp; Conditions
        </a>
        <a
          href="mailto:n8onelife@gmail.com"
          className="hover:text-[hsl(var(--fg))]"
          data-testid="profile-contact-link"
        >
          Contact
        </a>
      </section>
    </div>
  );
}

function IntelligentRemindersHint() {
  return (
    <div
      className="p-5 border hairline rounded-md bg-[hsl(var(--sand))]/40"
      data-testid="intelligent-reminders-panel"
    >
      <div className="text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--terracotta))] flex items-center gap-2">
        <span>Intelligent Reminders</span>
        <ProBadge />
      </div>
      <p className="mt-2 text-sm text-[hsl(var(--fg))]/85 leading-relaxed max-w-xl">
        N8One quietly learns when you actually complete your rituals. When your rhythm drifts, you&rsquo;ll
        get a gentle suggestion here — e.g. <em>&ldquo;You usually finish this in the evening. Move the
        reminder to 7:00 PM?&rdquo;</em> Nothing changes without your tap.
      </p>
      <p className="mt-2 text-xs text-[hsl(var(--muted-foreground))]">
        Suggestions appear here after a week or two of ritual data. No suggestions yet — keep showing up.
      </p>
    </div>
  );
}


function SacredAgingEntry({ profile, onOpen }) {
  const answers = profile?.sacred_aging || {};
  const isAnswered = (v) => {
    if (v == null) return false;
    if (typeof v === "string") return v.trim().length > 0;
    if (Array.isArray(v)) return v.length > 0;
    if (typeof v === "object") {
      const sel = Array.isArray(v.selected) ? v.selected : [];
      const note = typeof v.note === "string" ? v.note.trim() : "";
      return sel.length > 0 || note.length > 0;
    }
    return false;
  };
  let answered = 0;
  let total = 0;
  for (const s of SACRED_AGING_SECTIONS) {
    for (const p of s.prompts) {
      total += 1;
      if (isAnswered(answers?.[s.slug]?.[p.key])) answered += 1;
    }
  }
  const started = answered > 0;
  return (
    <section
      className="p-8 border hairline rounded-md bg-gradient-to-br from-[hsl(var(--sand))]/60 to-[hsl(var(--paper))] reveal reveal-2"
      data-testid="sacred-aging-entry"
    >
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div className="max-w-xl">
          <div className="text-[11px] uppercase tracking-[0.24em] text-[hsl(var(--terracotta))] inline-flex items-center gap-2">
            <Sparkles size={12} /> The Art Of Becoming
          </div>
          <h2 className="font-display text-3xl mt-2 italic">Your Intentional Self</h2>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2 leading-relaxed">
            Six deep reflections on who you're becoming, what makes life feel meaningful, what you're
            outgrowing, and the life you're intentionally creating. Not a questionnaire — a slow
            remembering. Your answers quietly shape how N8 shows up for you across rituals, whispers,
            and the way your future self is invited into the conversation.
          </p>
          <div
            className="mt-3 text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--muted-foreground))]"
            data-testid="sacred-aging-entry-progress"
          >
            {started ? `${answered} of ${total} reflections offered` : "Not yet opened"}
          </div>
        </div>
        <Button
          onClick={onOpen}
          data-testid="btn-open-sacred-aging"
          className="rounded-full bg-[hsl(var(--terracotta))] hover:bg-[hsl(var(--terracotta))]/90 text-[hsl(var(--paper))]"
        >
          {started ? "Continue" : "Begin the becoming"}
          <ChevronRight size={14} className="ml-2" />
        </Button>
      </div>
    </section>
  );
}

function BecomingEntry({ onOpen }) {
  const [count, setCount] = React.useState(null);
  const [preview, setPreview] = React.useState(null);
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await endpoints.becoming();
        if (cancelled) return;
        const list = Array.isArray(data) ? data : [];
        setCount(list.length);
        setPreview(list[0] || null);
      } catch {
        if (!cancelled) setCount(0);
      }
    })();
    return () => { cancelled = true; };
  }, []);
  const has = (count || 0) > 0;
  return (
    <section
      className="p-8 border hairline rounded-md bg-[hsl(var(--paper))] reveal reveal-2"
      data-testid="becoming-entry"
    >
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div className="max-w-xl">
          <div className="text-[11px] uppercase tracking-[0.24em] text-[hsl(var(--terracotta))] inline-flex items-center gap-2">
            <Camera size={12} /> Then &amp; Now
          </div>
          <h2 className="font-display text-3xl mt-2">Your Becoming Photos</h2>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2 leading-relaxed">
            A gallery of your Then and Now check-ins — a record of the way you keep showing up for yourself.
          </p>
          <div
            className="mt-3 text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--muted-foreground))]"
            data-testid="becoming-entry-count"
          >
            {count === null
              ? "Loading…"
              : has
              ? `${count} Then and Now moment${count === 1 ? "" : "s"}`
              : "No Then and Now yet"}
          </div>
        </div>
        <div className="flex items-center gap-4">
          {preview?.photo_before_path && preview?.photo_after_path && (
            <div className="hidden sm:grid grid-cols-2 gap-0.5 w-32 rounded-md overflow-hidden border hairline" aria-hidden>
              <img
                src={endpoints.fileUrl(preview.photo_before_path)}
                alt="then"
                className="w-full h-16 object-cover"
              />
              <img
                src={endpoints.fileUrl(preview.photo_after_path)}
                alt="now"
                className="w-full h-16 object-cover"
              />
            </div>
          )}
          <Button
            onClick={onOpen}
            data-testid="btn-open-becoming"
            className="rounded-full bg-[hsl(var(--terracotta))] hover:bg-[hsl(var(--terracotta))]/90 text-[hsl(var(--paper))]"
          >
            {has ? "Open gallery" : "See how it works"}
            <ChevronRight size={14} className="ml-2" />
          </Button>
        </div>
      </div>
    </section>
  );
}

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))]">{label}</span>
      {hint && (
        <span className="block mt-1 text-xs text-[hsl(var(--muted-foreground))] normal-case tracking-normal leading-relaxed">
          {hint}
        </span>
      )}
      <div className="mt-2">{children}</div>
    </label>
  );
}

/**
 * Reusable checkbox pill grid used by every life-area goal section in Profile.
 * Renders one clay-tinted pill per option. Selections write back to the
 * profile draft (`set(field, next)`) and the current selection list is read
 * via `val(field)`. Kept identical in feel to the original Skin Care Goals
 * so the block reads as one cohesive family.
 */
function GoalCheckGrid({
  label, hint, field, options, val, set,
  testidPrefix, testidCount, gridTestid, suffix,
}) {
  const list = val(field) || [];
  return (
    <Field label={label} hint={hint}>
      <div className="flex flex-wrap gap-2" data-testid={gridTestid}>
        {options.map((g) => {
          const on = list.includes(g.slug);
          return (
            <button
              key={g.slug}
              type="button"
              onClick={() => set(field, on ? list.filter((x) => x !== g.slug) : [...list, g.slug])}
              data-testid={`${testidPrefix}-${g.slug}`}
              aria-pressed={on}
              className={`inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-sm border hairline tab-link ${
                on
                  ? "bg-[hsl(var(--clay))] text-[hsl(var(--paper))] border-transparent"
                  : "hover:bg-[hsl(var(--sand))]"
              }`}
            >
              <span
                aria-hidden
                className={`inline-block w-3 h-3 rounded-sm border ${
                  on ? "bg-[hsl(var(--paper))] border-[hsl(var(--paper))]" : "border-[hsl(var(--muted-foreground))]/60"
                }`}
              >
                {on && (
                  <svg viewBox="0 0 12 12" className="w-3 h-3 text-[hsl(var(--clay))]" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M2.5 6.5l2.5 2.5 4.5-5" />
                  </svg>
                )}
              </span>
              {g.name}
            </button>
          );
        })}
      </div>
      {list.length > 0 && (
        <div className="mt-3 text-xs text-[hsl(var(--muted-foreground))]" data-testid={testidCount}>
          {list.length} {suffix}{list.length === 1 ? "" : "s"} selected
        </div>
      )}
    </Field>
  );
}



/**
 * UsernameInput — debounced availability check against
 * `/api/profile/username/check`. Shows a normalized preview with the "@"
 * prefix, an availability line, and clickable suggestions when a
 * requested handle is already claimed. Empty input is treated as "no
 * change" so we don't force members to name themselves on first save.
 */
function UsernameInput({ value, onChange, currentUsername }) {
  const [status, setStatus] = React.useState(null);   // { available, error, suggestions, username }
  const [checking, setChecking] = React.useState(false);
  const raw = (value || "").replace(/^@+/, "");
  React.useEffect(() => {
    // Skip the check when nothing has been typed or the handle matches
    // what the profile already has — showing a "taken" flash while typing
    // in your own current name would feel wrong.
    const trimmed = (raw || "").trim();
    if (!trimmed) { setStatus(null); return; }
    if (trimmed.toLowerCase() === (currentUsername || "").toLowerCase()) {
      setStatus({ available: true, username: trimmed.toLowerCase(), suggestions: [] });
      return;
    }
    let cancelled = false;
    setChecking(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await import("@/lib/api").then((m) => m.endpoints.checkUsername(trimmed));
        if (!cancelled) setStatus(data);
      } catch { /* silent */ }
      finally { if (!cancelled) setChecking(false); }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [raw, currentUsername]);

  const applySuggestion = (s) => onChange(s);

  return (
    <div className="space-y-2" data-testid="username-input-wrap">
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))] pointer-events-none">@</span>
        <Input
          value={raw}
          onChange={(e) => onChange(e.target.value)}
          placeholder="yourhandle"
          spellCheck={false}
          autoComplete="off"
          data-testid="input-username"
          className="bg-transparent pl-7"
        />
      </div>
      {raw.trim().length > 0 && (
        <div className="text-xs" data-testid="username-status">
          {checking && <span className="text-[hsl(var(--muted-foreground))]">Checking availability…</span>}
          {!checking && status?.available && (
            <span className="text-[hsl(var(--moss))]">
              @{status.username} is available.
            </span>
          )}
          {!checking && status && !status.available && (
            <span className="text-[hsl(var(--terracotta))]">
              {status.error || "That username is already taken."}
            </span>
          )}
        </div>
      )}
      {!checking && status && !status.available && Array.isArray(status.suggestions) && status.suggestions.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5" data-testid="username-suggestions">
          <span className="text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))] mr-1">Try</span>
          {status.suggestions.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => applySuggestion(s)}
              data-testid={`username-suggestion-${s}`}
              className="px-2.5 py-1 rounded-full text-xs border hairline bg-[hsl(var(--paper))] hover:bg-[hsl(var(--sand))]/50 tab-link"
            >
              @{s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
